import React, { useState, useContext, useEffect } from 'react';
import Layout from '../../components/admin/Layout';
import { LanguageContext } from '../../context/LanguageContext';
import { Tab } from '@headlessui/react';
import { FaPrint } from 'react-icons/fa';
import "../../styles/react-datepicker.css";
import axios from '../../utils/axios';
import SalesReport from '../../components/reports/SalesReport';
import ItemsReport from '../../components/reports/ItemsReport';
import CategoryReport from '../../components/reports/CategoryReport';
import TimeReport from '../../components/reports/TimeReport';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const Reports = () => {
    const { language } = useContext(LanguageContext);
    // Format date for input in YYYY-MM-DDTHH:MM format
    const formatDateTimeForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    // Get current date and time
    const currentDateTime = new Date();
    
    // Create a date for yesterday at the same time
    const yesterdayDateTime = new Date(currentDateTime);
    yesterdayDateTime.setDate(currentDateTime.getDate() - 1);
    
    // Set default start time to yesterday at current time
    const [startDate, setStartDate] = useState(formatDateTimeForInput(yesterdayDateTime));
    
    // Set default end time to current time
    const [endDate, setEndDate] = useState(formatDateTimeForInput(currentDateTime));
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [selectedReportIndex, setSelectedReportIndex] = useState(0);
    const [error, setError] = useState(null);
    const [reportType, setReportType] = useState('sales');
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get('/settings');
                setSettings(response.data);
            } catch (error) {
                console.error('Error fetching settings:', error);
                // Use default settings if API fails
                setSettings({
                    restaurant: {
                        name: 'Restaurant',
                        name_ar: 'المطعم',
                        phone: '',
                        address: '',
                        address_ar: '',
                        tax_number: '',
                    },
                    printing: {
                        printer_type: 'thermal',
                        paper_size: '80mm',
                        font_size: '12',
                        printer_name: '',
                    }
                });
            }
        };
        
        fetchSettings();
    }, []);

    const reports = [
        {
            id: 'sales',
            title: language === 'ar' ? 'تقرير المبيعات' : 'Sales Report',
            description: language === 'ar' 
                ? 'تحليل المبيعات اليومية والشهرية مع تفاصيل المنتجات الأكثر مبيعاً'
                : 'Daily and monthly sales analysis with top-selling items'
        },
        {
            id: 'items',
            title: language === 'ar' ? 'تقرير المنتجات' : 'Items Performance',
            description: language === 'ar'
                ? 'تحليل أداء المنتجات وتصنيفها حسب المبيعات والإيرادات'
                : 'Item performance analysis and ranking by sales and revenue'
        },
        {
            id: 'categories',
            title: language === 'ar' ? 'تقرير الأقسام' : 'Category Analysis',
            description: language === 'ar'
                ? 'تحليل أداء الأقسام ومساهمتها في المبيعات'
                : 'Category performance and contribution to sales'
        },
        {
            id: 'time',
            title: language === 'ar' ? 'تحليل الوقت' : 'Time Analysis',
            description: language === 'ar'
                ? 'تحليل أوقات الذروة وأنماط الطلبات'
                : 'Peak hours and order pattern analysis'
        }
    ];

    const selectedReport = reports[selectedReportIndex];

    const handleGenerateReport = async () => {
        try {
            setLoading(true);
            setError(null);

            // Parse the input dates
            const startDateTime = new Date(startDate);
            const endDateTime = new Date(endDate);
            
            // Format date in ISO format without timezone offset
            const formatDateForAPI = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');
                
                return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            };
            
            const startISO = formatDateForAPI(startDateTime);
            const endISO = formatDateForAPI(endDateTime);
            


            const response = await axios.post('reports/generate', {
                report_type: reportType,
                start_date: startISO,
                end_date: endISO,
                format: 'json'
            });


            
            // Ensure returns data exists for category, items, and sales reports
            if (reportType === 'categories' && !response.data.returns) {

                response.data.returns = {
                    total_amount: 0,
                    count: 0,
                    percentage: 0
                };
            }
            
            if (reportType === 'items' && !response.data.returns) {

                response.data.returns = {
                    total_amount: 0,
                    count: 0,
                    percentage: 0
                };
            }
            
            if (reportType === 'sales' && !response.data.returns) {

                response.data.returns = {
                    total_amount: 0,
                    count: 0,
                    percentage: 0
                };
            }
            
            if (reportType === 'time' && !response.data.returns) {

                response.data.returns = {
                    total_amount: 0,
                    count: 0,
                    percentage: 0
                };
            }

            // Format dates for display
            const formatDisplayDate = (date) => {
                // Create a human-readable date format
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                
                if (language === 'ar') {
                    // Convert to Arabic numerals and format
                    const toArabicNumeral = (num) => num.toString().replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
                    return `${toArabicNumeral(day)}/${toArabicNumeral(month)}/${toArabicNumeral(year)} ${toArabicNumeral(hours)}:${toArabicNumeral(minutes)}`;
                } else {
                    return `${day}/${month}/${year} ${hours}:${minutes}`;
                }
            };

            // Add date range to report data
            response.data.date_range = {
                start: formatDisplayDate(startDateTime),
                end: formatDisplayDate(endDateTime)
            };


            setReportData(response.data);
        } catch (err) {
            console.error('Error generating report:', err);
            setError(language === 'ar' 
                ? 'حدث خطأ أثناء إنشاء التقرير' 
                : 'An error occurred while generating the report'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadReport = async () => {
        try {
            setLoading(true);
            setError(null);

            // Format dates for API without timezone offset
            const formatDateForAPI = (dateStr) => {
                const date = new Date(dateStr);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                const seconds = String(date.getSeconds()).padStart(2, '0');
                
                return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            };
            
            const startISO = formatDateForAPI(startDate);
            const endISO = formatDateForAPI(endDate);

            const response = await axios.post('reports/generate', {
                report_type: reportType,
                start_date: startISO,
                end_date: endISO,
                format: 'excel'
            }, {
                responseType: 'blob'
            });

            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${reportType}_report_${startDate}_to_${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading report:', err);
            setError(language === 'ar' 
                ? 'حدث خطأ أثناء تحميل التقرير' 
                : 'An error occurred while downloading the report'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (index) => {
        setSelectedReportIndex(index);
        setReportType(reports[index].id);
        setReportData(null);
    };

    const renderReportData = () => {
        if (!reportData) return null;

        switch (reportType) {
            case 'sales':
                return <SalesReport data={reportData} language={language} settings={settings} />;
            case 'items':
                return <ItemsReport data={reportData} language={language} settings={settings} />;
            case 'categories':
                return <CategoryReport data={reportData} language={language} settings={settings} />;
            case 'time':
                return <TimeReport data={reportData} language={language} settings={settings} />;
            default:
                return null;
        }
    };

    return (
        <Layout>
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800">
                    {language === 'ar' ? 'التقارير' : 'Reports'}
                </h1>
                <p className="text-gray-600 mt-1">
                    {language === 'ar' 
                        ? 'تحليل وتقارير أداء المطعم' 
                        : 'Restaurant performance analysis and reports'}
                </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <Tab.Group selectedIndex={selectedReportIndex} onChange={handleTabChange}>
                    <Tab.List className="flex space-x-1 rounded-xl bg-gray-50 p-1">
                        {reports.map((report) => (
                            <Tab
                                key={report.id}
                                className={({ selected }) =>
                                    classNames(
                                        'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                                        'ring-white ring-opacity-60 ring-offset-2 focus:outline-none focus:ring-2',
                                        selected
                                            ? 'bg-white text-indigo-600 shadow'
                                            : 'text-gray-600 hover:bg-white/[0.12] hover:text-indigo-600'
                                    )
                                }
                            >
                                {report.title}
                            </Tab>
                        ))}
                    </Tab.List>

                    <div className="mt-8">
                        <div className="mb-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">{selectedReport.title}</h3>
                            <p className="text-gray-500">{selectedReport.description}</p>
                            <p className="text-sm text-gray-500 mt-2 print-date-range">
                                {language === 'ar' ? 'الفترة:' : 'Period:'}{' '}
                                {(() => {
                                    const date = new Date(startDate);
                                    const day = date.getDate().toString().padStart(2, '0');
                                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                    const year = date.getFullYear();
                                    const hours = date.getHours().toString().padStart(2, '0');
                                    const minutes = date.getMinutes().toString().padStart(2, '0');
                                    
                                    if (language === 'ar') {
                                        const toArabicNumeral = (num) => num.toString().replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
                                        return `${toArabicNumeral(day)}/${toArabicNumeral(month)}/${toArabicNumeral(year)} ${toArabicNumeral(hours)}:${toArabicNumeral(minutes)}`;
                                    }
                                    return `${day}/${month}/${year} ${hours}:${minutes}`;
                                })()}{' '}
                                {language === 'ar' ? 'إلى' : 'to'}{' '}
                                {(() => {
                                    const date = new Date(endDate);
                                    const day = date.getDate().toString().padStart(2, '0');
                                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                    const year = date.getFullYear();
                                    const hours = date.getHours().toString().padStart(2, '0');
                                    const minutes = date.getMinutes().toString().padStart(2, '0');
                                    
                                    if (language === 'ar') {
                                        const toArabicNumeral = (num) => num.toString().replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
                                        return `${toArabicNumeral(day)}/${toArabicNumeral(month)}/${toArabicNumeral(year)} ${toArabicNumeral(hours)}:${toArabicNumeral(minutes)}`;
                                    }
                                    return `${day}/${month}/${year} ${hours}:${minutes}`;
                                })()}
                            </p>
                            <button
                                onClick={() => window.print()}
                                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <FaPrint className={language === 'ar' ? 'ml-2' : 'mr-2'} />
                                {language === 'ar' ? 'طباعة التقرير' : 'Print Report'}
                            </button>
                        </div>

                        {/* Date Range and Controls */}
                        <div className="mb-6 bg-gray-50 rounded-lg p-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                            <div className="flex flex-wrap gap-4">
                                <div className="w-64">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {language === 'ar' ? 'من تاريخ والوقت' : 'From Date & Time'}
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className={`block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}
                                    />
                                </div>
                                <div className="w-64">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {language === 'ar' ? 'إلى تاريخ والوقت' : 'To Date & Time'}
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className={`block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}
                                    />
                                </div>
                            </div>

                            <div className={`mt-6 flex gap-3 ${language === 'ar' ? 'justify-start' : 'justify-end'}`}>
                                <button
                                    onClick={handleGenerateReport}
                                    disabled={loading}
                                    className={`
                                        inline-flex items-center px-4 py-2 rounded-md text-sm font-medium
                                        ${loading
                                            ? 'bg-indigo-400 cursor-not-allowed'
                                            : 'bg-indigo-600 hover:bg-indigo-700'
                                        }
                                        text-white transition-colors duration-200 ease-in-out
                                    `}
                                >
                                    {loading ? (
                                        <>
                                            <svg className={`animate-spin h-4 w-4 text-white ${language === 'ar' ? 'ml-2' : 'mr-2'}`} fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                                        </>
                                    ) : (
                                        <>
                                            <svg className={`h-4 w-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            {language === 'ar' ? 'إنشاء التقرير' : 'Generate Report'}
                                        </>
                                    )}
                                </button>

                                {reportData && selectedReport.id === 'sales' && (
                                    <button
                                        onClick={handleDownloadReport}
                                        disabled={loading}
                                        className={`
                                            inline-flex items-center px-4 py-2 border border-gray-300 rounded-md
                                            shadow-sm text-sm font-medium text-gray-700 bg-white
                                            hover:bg-gray-50 transition-colors duration-200 ease-in-out
                                            ${loading ? 'cursor-not-allowed opacity-50' : ''}
                                        `}
                                    >
                                        <svg className={`h-4 w-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        {language === 'ar' ? 'تحميل التقرير' : 'Download Report'}
                                    </button>
                                )}
                            </div>

                            {error && (
                                <div className="mt-4 text-sm text-red-600">
                                    {error}
                                </div>
                            )}
                        </div>

                        {/* Report Content */}
                        {renderReportData()}
                    </div>
                </Tab.Group>
            </div>
        </Layout>
    );
};

export default Reports;